import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import DynamicType from '@/plugins/DynamicTypePlugin';

/**
 * iOS Dynamic Type Categories
 * https://developer.apple.com/design/human-interface-guidelines/typography
 */
export type ContentSizeCategory = 
  | 'xSmall'        // Extra Small
  | 'small'         // Small
  | 'medium'        // Medium (Default)
  | 'large'         // Large
  | 'xLarge'        // Extra Large
  | 'xxLarge'       // Extra Extra Large
  | 'xxxLarge'      // Extra Extra Extra Large
  // Accessibility Sizes
  | 'accessibilityMedium'
  | 'accessibilityLarge'
  | 'accessibilityXLarge'
  | 'accessibilityXXLarge'
  | 'accessibilityXXXLarge';

interface DynamicTypePreferences {
  category: ContentSizeCategory;
  scale: number;
  isAccessibilitySize: boolean;
}

const CATEGORY_SCALES: Record<ContentSizeCategory, number> = {
  xSmall: 0.8,
  small: 0.9,
  medium: 1.0,      // Default
  large: 1.15,
  xLarge: 1.3,
  xxLarge: 1.5,
  xxxLarge: 1.7,
  // Accessibility
  accessibilityMedium: 1.85,
  accessibilityLarge: 2.0,
  accessibilityXLarge: 2.35,
  accessibilityXXLarge: 2.75,
  accessibilityXXXLarge: 3.15,
};

/**
 * Hook para detectar e responder a mudanças no Dynamic Type do iOS
 * 
 * No iOS nativo, usa UIContentSizeCategoryDidChangeNotification via plugin Capacitor
 * No web, simula via browser font settings e media queries
 */
export function useDynamicType(): DynamicTypePreferences {
  const [preferences, setPreferences] = useState<DynamicTypePreferences>(() => {
    const initialCategory = detectContentSizeCategory();
    return {
      category: initialCategory,
      scale: CATEGORY_SCALES[initialCategory],
      isAccessibilitySize: initialCategory.startsWith('accessibility'),
    };
  });

  useEffect(() => {
    let nativeListenerHandle: any = null;

    // iOS Nativo: Usa plugin Capacitor
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      console.log('[Dynamic Type] Using native iOS plugin');
      
      // Ler valor inicial do iOS
      DynamicType.getContentSizeCategory()
        .then(({ category, scale }) => {
          console.log('[Dynamic Type] Initial iOS category:', category, 'scale:', scale);
          
          const typedCategory = category as ContentSizeCategory;
          setPreferences({
            category: typedCategory,
            scale,
            isAccessibilitySize: category.startsWith('accessibility'),
          });
          
          applyFontScale(scale);
        })
        .catch((error) => {
          console.warn('[Dynamic Type] Native plugin not available, using fallback', error);
          // Fallback para detecção web
          const fallbackCategory = detectContentSizeCategory();
          setPreferences({
            category: fallbackCategory,
            scale: CATEGORY_SCALES[fallbackCategory],
            isAccessibilitySize: fallbackCategory.startsWith('accessibility'),
          });
        });

      // Listener para mudanças em tempo real
      DynamicType.addListener('contentSizeCategoryChanged', ({ category, scale }) => {
        console.log('[Dynamic Type] iOS category changed:', category, 'scale:', scale);
        
        const typedCategory = category as ContentSizeCategory;
        setPreferences({
          category: typedCategory,
          scale,
          isAccessibilitySize: category.startsWith('accessibility'),
        });
        
        applyFontScale(scale);
      }).then((handle) => {
        nativeListenerHandle = handle;
      });

      return () => {
        if (nativeListenerHandle) {
          nativeListenerHandle.remove();
        }
      };
    }

    // Web/Android Fallback: Usa detecção via resize e media queries
    console.log('[Dynamic Type] Using web fallback');
    
    // Aplicar scale inicial ao documento
    applyFontScale(preferences.scale);

    const handleResize = () => {
      const newCategory = detectContentSizeCategory();
      const newScale = CATEGORY_SCALES[newCategory];
      
      setPreferences({
        category: newCategory,
        scale: newScale,
        isAccessibilitySize: newCategory.startsWith('accessibility'),
      });
      
      applyFontScale(newScale);
    };

    // No web, detecta via resize e zoom
    window.addEventListener('resize', handleResize);
    
    // Detecta mudanças em preferências de acessibilidade
    const mediaQuery = window.matchMedia('(prefers-contrast: high)');
    const handleMediaChange = () => handleResize();
    
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleMediaChange);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleMediaChange);
      }
    };
  }, []);

  return preferences;
}

/**
 * Detecta a categoria de tamanho de conteúdo atual
 * Usado apenas como fallback quando plugin nativo não está disponível
 */
function detectContentSizeCategory(): ContentSizeCategory {
  // Fallback: detecta via computed font size do documento
  const baseFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
  
  // Mapeamento de font-size para categoria
  if (baseFontSize >= 48) return 'accessibilityXXXLarge';
  if (baseFontSize >= 42) return 'accessibilityXXLarge';
  if (baseFontSize >= 36) return 'accessibilityXLarge';
  if (baseFontSize >= 32) return 'accessibilityLarge';
  if (baseFontSize >= 28) return 'accessibilityMedium';
  if (baseFontSize >= 24) return 'xxxLarge';
  if (baseFontSize >= 22) return 'xxLarge';
  if (baseFontSize >= 20) return 'xLarge';
  if (baseFontSize >= 18) return 'large';
  if (baseFontSize >= 14) return 'small';
  if (baseFontSize < 14) return 'xSmall';
  
  return 'medium'; // Default
}

/**
 * Aplica o scale de fonte ao documento
 */
function applyFontScale(scale: number) {
  document.documentElement.style.setProperty('--font-scale', scale.toString());
  
  // Adiciona classe para referência
  const categories: ContentSizeCategory[] = [
    'xSmall', 'small', 'medium', 'large', 'xLarge', 'xxLarge', 'xxxLarge',
    'accessibilityMedium', 'accessibilityLarge', 'accessibilityXLarge', 
    'accessibilityXXLarge', 'accessibilityXXXLarge'
  ];
  
  // Remove classes antigas
  categories.forEach(cat => {
    document.documentElement.classList.remove(`text-size-${cat}`);
  });
  
  // Adiciona classe correspondente ao scale
  const category = Object.entries(CATEGORY_SCALES)
    .find(([_, s]) => s === scale)?.[0] as ContentSizeCategory;
  
  if (category) {
    document.documentElement.classList.add(`text-size-${category}`);
  }
}

/**
 * Retorna o tamanho de fonte ajustado para Dynamic Type
 * Útil para tamanhos customizados que precisam escalar
 */
export function scaledFontSize(basePx: number): string {
  const scale = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue('--font-scale') || '1'
  );
  return `${basePx * scale}px`;
}

/**
 * Helper para verificar se está em modo de acessibilidade
 */
export function isAccessibilitySize(): boolean {
  return document.documentElement.className.includes('accessibility');
}
